// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { Suspense } from "react";
import { navigateTo } from "./navigation";
import { RouteGuard } from "./RouteGuard";
import { PageLoader } from "../components/layout/PageLoader";
import type { AppRoute } from "../types/routing";
import { lazyWithStaleChunkReload } from "../utils/staleChunkReload";
import { TeamsOnlyRoute } from "../routes/TeamsOnlyRoute";

import { LoginPage } from "../features/auth/pages/LoginPage";

const OnboardingPage = lazyWithStaleChunkReload(() =>
  import("../features/onboarding/pages/OnboardingPage").then((m) => ({
    default: m.OnboardingPage,
  })),
);

const ContactsPage = lazyWithStaleChunkReload(() =>
  import("../features/crm/pages/ContactsPage").then((m) => ({
    default: m.ContactsPage,
  })),
);

const JourneysPage = lazyWithStaleChunkReload(() =>
  import("../features/work/pages/JourneysPage").then((m) => ({
    default: m.JourneysPage,
  })),
);

const ProspectingPage = lazyWithStaleChunkReload(() =>
  import("../features/work/pages/ProspectingPage").then((m) => ({
    default: m.ProspectingPage,
  })),
);

const RelationshipsPage = lazyWithStaleChunkReload(() =>
  import("../features/relationships/pages/RelationshipsPage").then((m) => ({
    default: m.RelationshipsPage,
  })),
);

const CommunicationsPage = lazyWithStaleChunkReload(() =>
  import("../features/communications/pages/CommunicationsPage").then((m) => ({
    default: m.CommunicationsPage,
  })),
);

const PropertyPage = lazyWithStaleChunkReload(() =>
  import("../features/property/pages/PropertiesPage").then((m) => ({
    default: m.PropertiesPage,
  })),
);

const VendorsPage = lazyWithStaleChunkReload(() =>
  import("../features/vendors/pages/VendorsPage").then((m) => ({
    default: m.VendorsPage,
  })),
);

const WorkOrdersPage = lazyWithStaleChunkReload(() =>
  import("../features/workOrders/pages/WorkOrdersPage").then((m) => ({
    default: m.WorkOrdersPage,
  })),
);

const WorkloadForecastsPage = lazyWithStaleChunkReload(() =>
  import("../features/workloadForecasts/pages/WorkloadForecastsPage").then(
    (m) => ({
      default: m.WorkloadForecastsPage,
    }),
  ),
);

const DelegationsPage = lazyWithStaleChunkReload(() =>
  import("../features/delegations/pages/DelegationsPage").then((m) => ({
    default: m.DelegationsPage,
  })),
);

const MorningBriefPage = lazyWithStaleChunkReload(() =>
  import("../features/work/pages/MorningBriefPage").then((m) => ({
    default: m.MorningBriefPage,
  })),
);

const SettingsPage = lazyWithStaleChunkReload(() =>
  import("../features/settings/pages/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);

const DocumentsPage = lazyWithStaleChunkReload(() =>
  import("../features/documents/pages/DocumentsPage").then((m) => ({
    default: m.DocumentsPage,
  })),
);

interface AppRoutesProps {
  route: AppRoute;
  isAuthenticated: boolean;
}

export function AppRoutes({ route, isAuthenticated }: AppRoutesProps) {
  if (!isAuthenticated || route.key === "login") {
    return <LoginPage />;
  }

  return (
    <RouteGuard
      isAllowed={isAuthenticated}
      title="Authentication Required"
      description="Sign in to access this area."
      actionText="Go to sign in"
      onAction={() => navigateTo("/")}
    >
      <Suspense fallback={<PageLoader />}>
        {route.key === "onboarding" && <OnboardingPage />}
        {route.key === "contacts" && <ContactsPage />}
        {route.key === "tasks" && <MorningBriefPage initialTab="brief" />}
        {route.key === "journeys" && <JourneysPage />}
        {route.key === "prospecting" && <ProspectingPage />}
        {route.key === "relationships" && <RelationshipsPage />}
        {route.key === "communications" && <CommunicationsPage />}
        {route.key === "property" && <PropertyPage />}
        {route.key === "vendors" && <VendorsPage />}
        {route.key === "workOrders" && <WorkOrdersPage />}
        {route.key === "workloadForecasts" && <WorkloadForecastsPage />}
        {route.key === "delegations" && (
          <TeamsOnlyRoute>
            <DelegationsPage />
          </TeamsOnlyRoute>
        )}
        {route.key === "insights" && <MorningBriefPage initialTab="brief" />}
        {route.key === "documents" && <DocumentsPage />}
        {route.key === "settings" && <SettingsPage />}
      </Suspense>
    </RouteGuard>
  );
}
